import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { jwtDecode } from 'jwt-decode'
import { ACCESS_TOKEN, API_BASE_URL, REFRESH_TOKEN } from '../../assets/constants'
import { clearPinBrowserFlags } from '../../util/pinBrowserFlags'
import { RefreshTokenResponse, SessionRequest } from '../../proto/generated/identity'
import { buildDeviceInfoSummary, getDeviceId } from '../../util/deviceIdentity'
import { clearLoginSessionWindow, isLoginSessionWindowValid } from '../../util/loginSessionWindow'
import { isAccessTokenValid } from '../../util/authentication'
import { isEmbeddedModeSet } from '../../util/general'
import { version as CLIENT_VERSION } from '../../../package.json'

/** Refresh ~90s before access JWT expiry (server skew). */
const REFRESH_BEFORE_EXPIRY_SEC = 90
/** How long embedded mode waits for Android/iOS to push a refreshed access JWT. */
const HOST_TOKEN_WAIT_MS = 4_000
/** After a failed host wait, fail fast until cooldown ends (avoids freezing the SPA). */
const HOST_TOKEN_WAIT_COOLDOWN_MS = 8_000

let proactiveTimer: ReturnType<typeof setTimeout> | null = null
let inFlightRefresh: Promise<boolean> | null = null
let inFlightHostTokenWait: Promise<string | null> | null = null
let hostTokenWaitFailedAt = 0
const accessTokenRefreshListeners = new Set<() => void>()

/**
 * Bumped on successful PIN unlock so in-flight refresh responses that started
 * before unlock cannot overwrite the unlocked access JWT with a pending one.
 */
let unlockGeneration = 0

function clearProactiveTimer() {
	if (proactiveTimer !== null) {
		clearTimeout(proactiveTimer)
		proactiveTimer = null
	}
}

export function scheduleProactiveAccessTokenRefresh() {
	clearProactiveTimer()
	const access = localStorage.getItem(ACCESS_TOKEN)
	const refresh = localStorage.getItem(REFRESH_TOKEN)
	if (!refresh) return
	if (!access) {
		void refreshAccessToken()
		return
	}
	try {
		const { exp } = jwtDecode<{ exp?: number }>(access)
		if (!exp) {
			void refreshAccessToken()
			return
		}
		const fireAtMs = (exp - REFRESH_BEFORE_EXPIRY_SEC) * 1000 - Date.now()
		if (fireAtMs <= 0) {
			void refreshAccessToken()
			return
		}
		proactiveTimer = setTimeout(() => {
			void refreshAccessToken()
		}, fireAtMs)
	} catch {
		void refreshAccessToken()
	}
}

function notifyAccessTokenRefreshed() {
	accessTokenRefreshListeners.forEach((listener) => listener())
}

function applyRefreshResponse(decoded: RefreshTokenResponse, startedAtGeneration: number) {
	// Drop stale refresh that finished after PIN unlock reminted an unlocked JWT.
	if (startedAtGeneration !== unlockGeneration) {
		return
	}
	localStorage.setItem(ACCESS_TOKEN, decoded.accessToken)
	scheduleProactiveAccessTokenRefresh()
	notifyAccessTokenRefreshed()
}

export function subscribeAccessTokenRefresh(listener: () => void): () => void {
	accessTokenRefreshListeners.add(listener)
	return () => accessTokenRefreshListeners.delete(listener)
}

/**
 * Embedded WebView has no refresh token — wait for the native host to push a new
 * access JWT via window.setAccessToken / localStorage.
 */
export function waitForValidAccessToken(timeoutMs: number = HOST_TOKEN_WAIT_MS): Promise<string | null> {
	const current = localStorage.getItem(ACCESS_TOKEN)
	if (current && isAccessTokenValid()) {
		return Promise.resolve(current)
	}
	if (inFlightHostTokenWait) {
		return inFlightHostTokenWait
	}
	if (Date.now() - hostTokenWaitFailedAt < HOST_TOKEN_WAIT_COOLDOWN_MS) {
		return Promise.resolve(null)
	}

	inFlightHostTokenWait = new Promise((resolve) => {
		const finish = (token: string | null) => {
			clearTimeout(timer)
			unsubscribe()
			inFlightHostTokenWait = null
			if (!token) {
				hostTokenWaitFailedAt = Date.now()
			} else {
				hostTokenWaitFailedAt = 0
			}
			resolve(token)
		}

		const unsubscribe = subscribeAccessTokenRefresh(() => {
			const token = localStorage.getItem(ACCESS_TOKEN)
			if (token && isAccessTokenValid()) {
				finish(token)
			}
		})

		const timer = setTimeout(() => {
			const token = localStorage.getItem(ACCESS_TOKEN)
			finish(token && isAccessTokenValid() ? token : null)
		}, timeoutMs)
	})

	return inFlightHostTokenWait
}

export async function ensureValidAccessToken(): Promise<string | null> {
	const access = localStorage.getItem(ACCESS_TOKEN)
	if (access && isAccessTokenValid()) {
		return access
	}
	if (localStorage.getItem(REFRESH_TOKEN)) {
		const ok = await refreshAccessToken()
		if (!ok) {
			return null
		}
		return localStorage.getItem(ACCESS_TOKEN)
	}
	if (isEmbeddedModeSet()) {
		return waitForValidAccessToken()
	}
	return null
}

export function persistAuthFromLoginResponse(response: { accessToken: string; refreshToken?: string }) {
	localStorage.setItem(ACCESS_TOKEN, response.accessToken)
	if (response.refreshToken) {
		localStorage.setItem(REFRESH_TOKEN, response.refreshToken)
	}
	// Session window is opened only after PIN unlock / in-app PIN setup completes.
	scheduleProactiveAccessTokenRefresh()
	notifyAccessTokenRefreshed()
}

/** True when access JWT pinSession claim is unlocked (or missing on legacy tokens). */
export function isAccessTokenPinUnlocked(accessToken?: string | null): boolean {
	const token = accessToken ?? localStorage.getItem(ACCESS_TOKEN)
	if (!token) return false
	try {
		const claims = jwtDecode<{ pinSession?: string }>(token)
		const pinSession = claims.pinSession
		return !pinSession || pinSession === 'unlocked'
	} catch {
		return false
	}
}

/**
 * Persist reminted access token after PIN unlock / setup / forgot-PIN.
 * Returns false when token is missing (caller must stay on PIN UI).
 */
export function persistUnlockedAccessToken(accessToken: string | undefined | null): boolean {
	if (!accessToken) return false
	unlockGeneration += 1
	clearProactiveTimer()
	localStorage.setItem(ACCESS_TOKEN, accessToken)
	hostTokenWaitFailedAt = 0
	scheduleProactiveAccessTokenRefresh()
	notifyAccessTokenRefreshed()
	return true
}

/**
 * Drop the access JWT so a fresh login/OAuth request is not sent with a restricted
 * (pending/onboarding) Bearer that would be rejected by PinSessionAccessControlFilter.
 * Leaves refresh token alone until the new login replaces it.
 */
export function clearAccessToken() {
	clearProactiveTimer()
	localStorage.removeItem(ACCESS_TOKEN)
}

export function clearAuthSession() {
	clearProactiveTimer()
	localStorage.removeItem(ACCESS_TOKEN)
	localStorage.removeItem(REFRESH_TOKEN)
	clearPinBrowserFlags()
	clearLoginSessionWindow()
	inFlightRefresh = null
	inFlightRefreshResult = null
	unlockGeneration += 1
	accessTokenRefreshListeners.clear()
}

export type RefreshAccessTokenResult =
	| { status: 'ok' }
	| { status: 'no_token' }
	/** Refresh token rejected / session revoked — safe to clear local auth. */
	| { status: 'unauthorized' }
	/** Network / 5xx / decode issues — do not wipe the session. */
	| { status: 'transient' }

let inFlightRefreshResult: Promise<RefreshAccessTokenResult> | null = null

function isUnauthorizedRefreshError(error: unknown): boolean {
	const status = (error as { response?: { status?: number } })?.response?.status
	// Identity maps AuthenticationFailedException to 400 (no httpResponseCode on the exception).
	// 401/403 cover gateway / Spring Security style rejections.
	return status === 400 || status === 401 || status === 403
}

/**
 * Uses refresh token + same device id/info as login. Plain axios to avoid interceptor loops.
 * Prefer this over {@link refreshAccessToken} when deciding PIN vs full login.
 */
export async function refreshAccessTokenResult(): Promise<RefreshAccessTokenResult> {
	if (inFlightRefreshResult) return inFlightRefreshResult

	const refresh = localStorage.getItem(REFRESH_TOKEN)
	if (!refresh) return { status: 'no_token' }

	const startedAtGeneration = unlockGeneration

	inFlightRefreshResult = (async (): Promise<RefreshAccessTokenResult> => {
		try {
			const body = SessionRequest.fromPartial({
				refreshToken: refresh,
				deviceId: getDeviceId(),
				deviceInfo: await buildDeviceInfoSummary(),
			})
			const res = await axios.post(`${API_BASE_URL}/auth/refresh`, JSON.stringify(SessionRequest.toJSON(body)), {
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/x-protobuf',
					'X-Client-Version': CLIENT_VERSION,
				},
				responseType: 'arraybuffer',
				validateStatus: (s) => s === 200,
			})
			const decoded = RefreshTokenResponse.decode(new Uint8Array(res.data))
			if (!decoded.accessToken) return { status: 'transient' }
			applyRefreshResponse(decoded, startedAtGeneration)
			// Stale after unlock: treat as ok so callers don't wipe the unlocked session.
			if (startedAtGeneration !== unlockGeneration) {
				return { status: 'ok' }
			}
			return { status: 'ok' }
		} catch (error) {
			if (isUnauthorizedRefreshError(error)) {
				return { status: 'unauthorized' }
			}
			return { status: 'transient' }
		} finally {
			inFlightRefreshResult = null
			inFlightRefresh = null
		}
	})()

	// Keep boolean helper in sync when callers share the same in-flight request.
	inFlightRefresh = inFlightRefreshResult.then((r) => r.status === 'ok')

	return inFlightRefreshResult
}

/**
 * Uses refresh token + same device id/info as login. Plain axios to avoid interceptor loops.
 */
export async function refreshAccessToken(): Promise<boolean> {
	if (inFlightRefresh) return inFlightRefresh
	const result = await refreshAccessTokenResult()
	return result.status === 'ok'
}

const REFRESH_RETRY_DELAY_MS = 400

/**
 * Retry transient refresh failures up to `maxAttempts`.
 * Stops early on ok / unauthorized / no_token.
 */
export async function refreshAccessTokenWithRetries(
	maxAttempts: number = 3,
	delayMs: number = REFRESH_RETRY_DELAY_MS,
): Promise<RefreshAccessTokenResult> {
	let last: RefreshAccessTokenResult = { status: 'no_token' }
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		last = await refreshAccessTokenResult()
		if (last.status !== 'transient') {
			return last
		}
		if (attempt < maxAttempts) {
			await new Promise((r) => setTimeout(r, delayMs))
		}
	}
	return last
}

/**
 * True when a 434 should not force PIN again (already unlocked + daily window open).
 */
export function shouldIgnorePinSessionRequired(): boolean {
	return isAccessTokenPinUnlocked() && isLoginSessionWindowValid()
}

const AUTH_PATH_SKIP_REFRESH = ['/auth/login', '/auth/refresh', '/auth/oauth/token/validate', '/auth/login-url']

function shouldAttemptRefreshOn401(fullUrl: string): boolean {
	return !AUTH_PATH_SKIP_REFRESH.some((p) => fullUrl.includes(p))
}

export async function attempt401RefreshRetry(
	client: AxiosInstance,
	error: any
): Promise<AxiosResponse | null> {
	const cfg = error?.config
	if (error?.response?.status !== 401 || !cfg || cfg._authRefreshTried) {
		return null
	}
	const fullUrl = String((cfg.baseURL ?? '') + (cfg.url ?? ''))
	if (!shouldAttemptRefreshOn401(fullUrl)) {
		return null
	}
	cfg._authRefreshTried = true

	let token: string | null = null
	if (localStorage.getItem(REFRESH_TOKEN)) {
		const ok = await refreshAccessToken()
		if (ok) {
			token = localStorage.getItem(ACCESS_TOKEN)
		}
	} else if (isEmbeddedModeSet()) {
		token = await waitForValidAccessToken()
	}

	if (!token) {
		return null
	}
	cfg.headers = { ...cfg.headers, Authorization: `Bearer ${token}` }
	return client.request(cfg)
}
